import { defineStore } from "pinia";
import { useGetLatLngForZone } from "~/composables/zone/useGetLatLngForZone";

export const useZoneStore = defineStore("zone", {
  state: () => ({
    subcounties: [],
    miniMarkets: [],
    latLngByZoneId: {},
    isLoaded: false,
    isLoading: false,
    loadError: null,
    calculatedMiniMarketIds: [],
  }),
  getters: {
    professionIdsWithMiniMarkets: (state) => {
      return state.miniMarkets
        .map((miniMarket) => miniMarket.professions)
        .flat()
        .map((profession) => profession.id);
    },
    miniMarketForProfession: (state) => (profession) => {
      return state.miniMarkets.find((miniMarket) =>
        miniMarket.professions.some((p) => p.id === profession.id)
      );
    },
    professionsForMiniMarket: (state) => (miniMarketId) => {
      const miniMarket = state.miniMarkets.find(
        (miniMarket) => miniMarket.id === miniMarketId
      );

      if (!miniMarket) {
        return [];
      }

      return miniMarket.professions;
    },
    zoneForProfessionAndCity: (state) => (profession, city) => {
      const miniMarket = state.miniMarkets.find((miniMarket) =>
        miniMarket.professions.some((p) => p.id === profession.id)
      );

      if (!miniMarket) {
        return null;
      }

      if (!miniMarket.zones) {
        throw new Error(`Zones not loaded for mini market #${miniMarket.id}.`);
      }

      return miniMarket.zones.find((zone) =>
        zone.subcounties.some(
          (subcounty) =>
            subcounty.id === city.subcountyId ||
            (city.countyId === "1" && subcounty.countyId === "1")
        )
      );
    },
  },
  actions: {
    async loadZonesForMiniMarketId(miniMarketId) {
      try {
        this.loadError = null;
        this.isLoading = true;

        if (!miniMarketId) {
          return;
        }

        const thisMiniMarket = this.miniMarkets.find(
          (miniMarket) => miniMarket.id === miniMarketId
        );

        if (!thisMiniMarket) {
          throw new Error(`Mini market #${miniMarketId} not found`);
        }

        if (thisMiniMarket?.zones?.length) {
          return;
        }

        const GET_ZONES_FOR_MINI_MARKET = await import(
          "./queries/getZonesForMiniMarketId.graphql"
        );

        const { miniMarketById } = await this.$nuxt.$query(
          GET_ZONES_FOR_MINI_MARKET,
          {
            id: miniMarketId,
          }
        );
        this.miniMarkets = this.miniMarkets.map((existingMiniMarket) => {
          if (existingMiniMarket.id !== miniMarketId) {
            return existingMiniMarket;
          }

          return {
            ...existingMiniMarket,
            ...miniMarketById,
          };
        });
      } catch (err) {
        console.error(err);
        this.$nuxt.$sentry.captureException(err, {
          tags: {
            action: "load_zones_for_mini_market_id",
          },
        });
        this.loadError = true;
        return Promise.reject(err);
      } finally {
        this.isLoading = false;
      }
    },
    async loadMiniMarkets(forceReload = false) {
      if (this.isLoaded && !forceReload) {
        return;
      }

      try {
        this.loadError = false;
        this.isLoading = true;
        const GET_MINI_MARKETS = await import(
          "./queries/getMiniMarkets.graphql"
        );
        const { miniMarkets } = await this.$nuxt.$query(GET_MINI_MARKETS);

        this.miniMarkets = miniMarkets;
        this.isLoaded = true;
      } catch (err) {
        console.error(err);
        this.$nuxt.$sentry.captureException(err, {
          tags: {
            action: "load_mini_markets",
          },
        });

        this.isLoaded = false;
        this.loadError = true;

        return Promise.reject(err);
      } finally {
        this.isLoading = false;
      }
    },
    async loadSubcounties() {
      if (!this.subcounties.length) {
        try {
          this.isLoading = true;
          const GET_SUBCOUNTIES = await import(
            "./queries/getSubcounties.graphql"
          );
          const { subcounties } = await this.$nuxt.$query(GET_SUBCOUNTIES);
          this.subcounties = subcounties.map((s) => {
            const geoJson = s.geometryGeoJson;
            let latLng = [];
            if (geoJson.type === "Polygon") {
              latLng = geoJson.coordinates[0].map((coordinate) => [
                coordinate[1],
                coordinate[0],
              ]);
            }

            if (geoJson.type === "MultiPolygon") {
              latLng = geoJson.coordinates.map((coords) =>
                coords[0].map((coordinate) => [coordinate[1], coordinate[0]])
              );
            }

            return {
              ...s,
              latLng,
            };
          });
        } catch (err) {
          console.error(err);
          this.$nuxt.$sentry.captureException(err, {
            tags: {
              action: "load_subcounties",
            },
          });

          this.isLoaded = false;
          this.loadError = true;

          return Promise.reject(err);
        } finally {
          this.isLoading = false;
        }
      }
    },
    calculateLatLngsForMiniMarketId(miniMarketId) {
      const { getLatLngForZone } = useGetLatLngForZone();
      if (!miniMarketId) {
        return;
      }
      if (this.calculatedMiniMarketIds.includes(miniMarketId)) {
        return;
      }

      this.miniMarkets = this.miniMarkets.map((miniMarket) => {
        if (miniMarketId !== miniMarket.id) {
          return miniMarket;
        }

        if (!miniMarket.zones) {
          throw new Error(
            `Zones not loaded for mini market #${miniMarket.id}.`
          );
        }

        return {
          ...miniMarket,
          zones: miniMarket.zones.map((zone) => ({
            ...zone,
            latLng: getLatLngForZone(zone),
          })),
        };
      });

      this.calculatedMiniMarketIds.push(miniMarketId);
    },
  },
});
